(() => {
  if (customElements.get('boutique-cart')) return;
  const escapeHTML = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const passiveCartParameters = new Set(['preview_theme_id', 'preview_key', '_ab', '_fd', '_sc', 'pb', '_pos', '_sid', '_ss', 'fbclid', 'gclid', 'gclsrc', 'msclkid', 'srsltid']);
  class BoutiqueCart extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.config = JSON.parse(this.querySelector('[data-config]').textContent);
      this.i18n = this.config.i18n;
      this.dialog = this.querySelector('dialog');
      this.root = window.Shopify?.routes?.root || '/';
      this.abort = new AbortController();
      this.version = 0;
      const options = {signal: this.abort.signal};
      document.addEventListener('click', e => {
        const link = e.target.closest?.('a[href]');
        if (!link || this.contains(link) || e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
        if (this.isCartViewLink(link)) {
          e.preventDefault(); e.stopImmediatePropagation(); this.open(link);
        }
      }, {...options, capture:true});
      document.addEventListener('submit', e => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement) || this.contains(form)) return;
        const url = new URL(form.action, location.href);
        if (url.origin !== location.origin || !/\/cart\/add(?:\.js)?\/?$/.test(url.pathname)) return;
        if (e.submitter?.name === 'checkout') return;
        e.preventDefault(); e.stopImmediatePropagation();
        if (this.busy || e.submitter?.disabled || e.submitter?.getAttribute('aria-disabled') === 'true') return;
        const data = new FormData(form);
        data.delete('sections'); data.delete('sections_url');
        this.open(e.submitter, false);
        this.mutate('cart/add.js', data);
      }, {...options,capture:true});
      this.addEventListener('click', e => {
        const button = e.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-close')) this.dialog.close();
        if (button.dataset.key && !this.busy) {
          const item = this.cart?.items.find(i => i.key === button.dataset.key);
          if (!item) return;
          this.restoreFocus = {key:item.key,action:button.dataset.action};
          this.mutate('cart/change.js', {id:item.key,quantity:button.dataset.action === 'remove' ? 0 : item.quantity + Number(button.dataset.delta)});
        }
        if (button.hasAttribute('data-add') && !this.busy) {
          const select = button.closest('.bc-product').querySelector('select');
          if (select.value) this.mutate('cart/add.js',{items:[{id:Number(select.value),quantity:1}]});
        }
      }, options);
      this.addEventListener('change', e => {
        if (!e.target.matches('.bc-product select')) return;
        const selected = e.target.selectedOptions[0];
        e.target.closest('.bc-product').querySelector('.bc-prices').textContent = this.money(Number(selected.dataset.price));
      }, options);
      this.querySelector('.bc-code')?.addEventListener('submit', e => {
        e.preventDefault();
        const code = this.querySelector('#bc-code').value.trim();
        if (code && !this.busy) this.mutate('cart/update.js',{discount:code},code);
      }, options);
      this.dialog.addEventListener('click', e => {
        if (e.target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          if (e.clientX < rect.left || e.clientX > rect.right) this.dialog.close();
        }
      }, options);
      this.dialog.addEventListener('close', () => {
        this.stopReservationTimer();
        document.body.classList.remove('bc-open');
        if (this.opener?.isConnected) this.opener.focus({preventScroll:true});
      }, options);
      window.addEventListener('pageshow', () => { if (this.dialog.open) this.load(); }, options);
      document.addEventListener('boutique-cart:open', () => this.open(), options);
    }
    disconnectedCallback() { this.abort?.abort(); this.stopReservationTimer(); this.ready = false; document.body.classList.remove('bc-open'); }
    isCartViewLink(link) {
      let url;
      try { url = new URL(link.href, location.href); } catch { return false; }
      if (url.origin !== location.origin) return false;
      const cartPath = new URL(window.routes?.cart_url || `${this.root}cart`, location.href).pathname.replace(/\/+$/, '');
      const path = url.pathname.replace(/\/+$/, '');
      if (path !== cartPath && path !== '/cart') return false;
      return [...url.searchParams.keys()].every(key => passiveCartParameters.has(key) || /^utm_[a-z0-9_]+$/i.test(key));
    }
    money(cents) { return new Intl.NumberFormat('fr-FR',{style:'currency',currency:this.cart?.currency || window.Shopify?.currency?.active || 'EUR'}).format(cents/100); }
    async request(path, body) {
      const isForm = body instanceof FormData;
      const response = await fetch(this.root + path, {
        method:body ? 'POST' : 'GET',credentials:'same-origin',cache:'no-store',
        ...(body ? {body:isForm ? body : JSON.stringify(body),headers:isForm ? {'Accept':'application/json'} : {'Content-Type':'application/json','Accept':'application/json'}} : {})
      });
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : this.i18n.update_error);
      return data;
    }
    async readCart() {
      const [cart, sections] = await Promise.all([
        this.request('cart.js'),
        this.request('?sections=alma-cart-data').catch(() => null)
      ]);
      const markup = sections?.['alma-cart-data'];
      if (typeof markup === 'string') {
        try {
          const document = new DOMParser().parseFromString(markup, 'text/html');
          const prices = JSON.parse(document.querySelector('[data-alma-cart-prices]').textContent);
          cart.reference_prices = prices.items;
        } catch {}
      }
      return cart;
    }
    referenceTotal(item) {
      const price = this.cart.reference_prices?.find(reference => reference.key === item.key && reference.quantity === item.quantity);
      const compare = item.selling_plan_allocation ? 0 : Number(price?.compare_at_price || 0) * item.quantity;
      return Math.max(item.original_line_price, Number.isFinite(compare) ? compare : 0);
    }
    percentage(reference, current) {
      if (!Number.isFinite(reference) || !Number.isFinite(current) || reference <= current || reference <= 0) return 0;
      return Math.min(100, Math.max(0, Math.round((reference - current) * 100 / reference)));
    }
    reservationStorageKey(cart) {
      const token = String(cart.token || '').split('?')[0];
      return token ? `alma-cart-reservation:${location.host}:${token}` : null;
    }
    syncReservation(cart) {
      this.stopReservationTimer();
      this.reservationDeadline = null;
      const banner = this.querySelector('[data-reservation]');
      if (!banner) return;
      banner.hidden = true;
      const storageKey = this.reservationStorageKey(cart);
      const previousKey = this.reservationKey;
      this.reservationKey = storageKey;
      try {
        if (!cart.item_count) {
          if (previousKey) window.sessionStorage.removeItem(previousKey);
          if (storageKey) window.sessionStorage.removeItem(storageKey);
          return;
        }
        if (!storageKey) return;
        const duration = 10 * 60 * 1000;
        const now = Date.now();
        const saved = window.sessionStorage.getItem(storageKey);
        const deadline = saved === null ? now + duration : Number(saved);
        if (!Number.isFinite(deadline) || deadline <= 0 || deadline > now + duration) return;
        if (saved === null) window.sessionStorage.setItem(storageKey, String(deadline));
        this.reservationDeadline = deadline;
        banner.hidden = false;
        this.updateReservationTimer();
        this.startReservationTimer();
      } catch {
        this.reservationDeadline = null;
        banner.hidden = true;
      }
    }
    updateReservationTimer() {
      if (!this.reservationDeadline) return;
      const banner = this.querySelector('[data-reservation]');
      if (!banner) return;
      const remaining = Math.max(0, Math.ceil((this.reservationDeadline - Date.now()) / 1000));
      const minutes = String(Math.floor(remaining / 60)).padStart(2, '0');
      const seconds = String(remaining % 60).padStart(2, '0');
      banner.querySelector('[data-reservation-time]').textContent = `${minutes}:${seconds}`;
      banner.querySelector('[data-reservation-label]').textContent = remaining ? this.i18n.reservation_active : this.i18n.reservation_expired;
      banner.dataset.expired = String(remaining === 0);
      if (!remaining) this.stopReservationTimer();
    }
    startReservationTimer() {
      this.stopReservationTimer();
      if (this.dialog.open && this.reservationDeadline > Date.now()) {
        this.reservationInterval = window.setInterval(() => this.updateReservationTimer(), 1000);
      }
    }
    stopReservationTimer() {
      if (this.reservationInterval !== undefined) window.clearInterval(this.reservationInterval);
      this.reservationInterval = undefined;
    }
    open(opener, refresh=true) {
      if (!this.dialog.open) {
        this.opener = opener || document.activeElement;
        this.dialog.showModal();
        document.body.classList.add('bc-open');
        this.querySelector('[data-close]').focus({preventScroll:true});
      }
      if (this.cart) this.syncReservation(this.cart);
      if (refresh && !this.busy) this.load();
    }
    setBusy(value) {
      this.busy = value;
      this.dialog.setAttribute('aria-busy',String(value));
      this.querySelectorAll('button:not([data-close]),select,input').forEach(node => { node.disabled=value; });
    }
    message(text, visible=false) {
      const status = this.querySelector('.bc-message');
      status.textContent = text;
      status.classList.toggle('visually-hidden', !visible);
    }
    async load() {
      if (this.busy) return;
      this.setBusy(true); this.message(this.i18n.updating_cart);
      try { this.render(await this.readCart()); this.message(''); }
      catch { this.message(this.i18n.load_error, true); this.querySelector('.bc-items').innerHTML=`<p><a href="${escapeHTML(this.root)}cart">${escapeHTML(this.i18n.open_cart)}</a></p>`; }
      finally { this.setBusy(false); }
    }
    async mutate(path, body, code) {
      if (this.busy) return;
      this.setBusy(true); this.message(this.i18n.updating);
      try {
        await this.request(path,body);
        const cart = await this.readCart();
        this.render(cart);
        if (code) {
          const applied = (cart.discount_codes || []).find(x => x.code.toLowerCase() === code.toLowerCase());
          this.message(applied?.applicable ? this.i18n.code_applied : this.i18n.code_not_applied, true);
        } else this.message(this.i18n.updated);
      } catch (error) {
        try { this.render(await this.readCart()); } catch {}
        this.message(error.message, true);
      } finally {
        this.setBusy(false);
        if (this.restoreFocus) {
          const {key,action}=this.restoreFocus;
          const target=[...this.querySelectorAll('button[data-key]')].find(b=>b.dataset.key===key && b.dataset.action===action);
          (target || this.querySelector('[data-close]')).focus({preventScroll:true});
          this.restoreFocus=null;
        }
      }
    }
    render(cart) {
      this.cart=cart; this.version++;
      this.dialog.dataset.empty=String(cart.item_count === 0);
      this.syncReservation(cart);
      this.querySelector('[data-total]').textContent=this.money(cart.total_price);
      this.querySelector('.bc-footer').hidden=!cart.item_count;
      this.querySelector('.bc-family').hidden=!cart.item_count;
      const items=this.querySelector('.bc-items');
      if (!cart.item_count) {
        items.innerHTML=`<div class="bc-empty"><span class="bc-empty-icon" aria-hidden="true"><svg viewBox="0 0 48 48" width="52" height="52" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M12 16h24l3 25H9l3-25Z"/><path d="M17 19V12a7 7 0 0 1 14 0v7"/></svg></span><h3>${escapeHTML(this.i18n.empty_title)}</h3><p class="bc-small">${escapeHTML(this.i18n.empty_text)}</p><a href="${escapeHTML(this.config.collectionUrl)}">${escapeHTML(this.i18n.explore)}</a></div>`;
      } else items.innerHTML=cart.items.map(item=>{
        const image=item.featured_image?.url || item.image;
        const options=item.variant_title && item.variant_title!=='Default Title' ? `<p class="bc-variant">${escapeHTML(item.variant_title)}</p>` : '';
        const properties=Object.entries(item.properties || {}).filter(([key,value])=>!key.startsWith('_') && value).map(([key,value])=>`<p class="bc-variant">${escapeHTML(key)}: ${escapeHTML(value)}</p>`).join('');
        const plan=item.selling_plan_allocation?.selling_plan?.name;
        const reference=this.referenceTotal(item);
        const percentage=this.percentage(reference,item.final_line_price);
        return `<article class="bc-item"><a href="${escapeHTML(item.url)}">${image ? `<img src="${escapeHTML(image)}" alt="${escapeHTML(item.product_title)}" width="88" height="88">` : ''}</a><div class="bc-item-info"><a class="bc-item-title" href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a>${options}${properties}${plan ? `<p class="bc-variant">${escapeHTML(plan)}</p>` : ''}<div class="bc-prices">${reference>item.final_line_price ? `<s>${this.money(reference)}</s>` : ''}<strong>${this.money(item.final_line_price)}</strong>${percentage ? `<span class="bc-saving">−${percentage} %</span>` : ''}</div><div class="bc-item-bottom"><div class="bc-quantity"><button type="button" data-key="${escapeHTML(item.key)}" data-action="minus" data-delta="-1" aria-label="${escapeHTML(this.i18n.decrease)} ${escapeHTML(item.product_title)}">−</button><span aria-label="${escapeHTML(this.i18n.quantity)}">${item.quantity}</span><button type="button" data-key="${escapeHTML(item.key)}" data-action="plus" data-delta="1" aria-label="${escapeHTML(this.i18n.increase)} ${escapeHTML(item.product_title)}">+</button></div><button class="bc-remove" type="button" data-key="${escapeHTML(item.key)}" data-action="remove" aria-label="${escapeHTML(this.i18n.remove)} ${escapeHTML(item.product_title)}"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 10v7m4-7v7"/></svg></button></div></div></article>`;
      }).join('');
      const referenceTotal=cart.items.reduce((total,item)=>total+this.referenceTotal(item),0);
      const totalSaving=Math.max(0,referenceTotal-cart.total_price);
      this.querySelector('.bc-discounts').innerHTML=totalSaving ? `<div class="bc-discount-row"><span>${escapeHTML(this.i18n.discount_saving)}</span><strong class="bc-saving">−${this.money(totalSaving)}</strong></div>` : '';
      const cartIcon=document.querySelector('#cart-icon-bubble');
      if(cartIcon && !cartIcon.querySelector('.cart-count-bubble') && cart.item_count){
        const bubble=document.createElement('div');
        bubble.className='cart-count-bubble';
        bubble.innerHTML='<span aria-hidden="true"></span><span class="visually-hidden"></span>';
        cartIcon.appendChild(bubble);
      }
      document.querySelectorAll('.cart-count-bubble').forEach(bubble=>{
        bubble.hidden=!cart.item_count;
        const number=bubble.querySelector('[aria-hidden="true"]'); if(number) number.textContent=cart.item_count;
        const accessible=bubble.querySelector('.visually-hidden'); if(accessible) accessible.textContent=`${cart.item_count} ${this.i18n.items}`;
      });
      this.renderReward();
      this.recommend(this.version);
      document.dispatchEvent(new CustomEvent('boutique-cart:updated',{detail:{cart}}));
    }
    renderReward() {
      const element=this.querySelector('.bc-reward');
      element.hidden=true; element.innerHTML='';
      if (!this.cart.item_count) return;
      if (this.config.rewardEnabled) {
        const offer=this.config.promotion;
        element.innerHTML=`<div><strong>${escapeHTML(offer.title)}</strong></div><div>${escapeHTML(offer.tierOne)}<br>${escapeHTML(offer.tierTwo)}<br>${escapeHTML(offer.tierThree)}</div><p>${escapeHTML(offer.instructions)}</p><p>${escapeHTML(offer.terms)}</p>`;
        element.hidden=false;
      } else if (this.config.shippingGoal>0 && this.cart.currency===this.config.shopCurrency) {
        const remaining=Math.max(0,this.config.shippingGoal-this.cart.total_price);
        element.innerHTML=`<div>${remaining ? escapeHTML(this.i18n.shipping_remaining).replace('__amount__', this.money(remaining)) : escapeHTML(this.i18n.shipping_reached)}</div><progress max="${this.config.shippingGoal}" value="${Math.min(this.cart.total_price,this.config.shippingGoal)}" aria-label="${escapeHTML(this.i18n.shipping_progress)}"></progress>`;
        element.hidden=false;
      }
    }
    async recommend(version) {
      const section=this.querySelector('.bc-recommendations'); section.hidden=true;
      if (!this.config.recommendations || !this.cart.item_count) return;
      const seed=this.cart.items[0].product_id;
      let products=[];
      try {
        const data=await this.request(`recommendations/products.json?product_id=${encodeURIComponent(seed)}&limit=10&intent=related`);
        products=data.products || [];
      } catch {}
      if (version!==this.version || !this.isConnected) return;
      const excluded=new Set([...this.cart.items.map(i=>String(i.product_id)),String(this.dataset.currentProduct)]);
      const seen=new Set();
      products=[...products,...this.config.fallback].filter(p=>{
        const id=String(p.id);
        if (seen.has(id) || excluded.has(id) || !p.variants?.some(v=>v.available)) return false;
        seen.add(id); return true;
      }).slice(0,3);
      this.querySelector('.bc-products').innerHTML=products.map(p=>{
        const variants=p.variants.filter(v=>v.available);
        const image=p.featured_image || p.image;
        return `<article class="bc-product">${image ? `<a href="${escapeHTML(p.url)}"><img src="${escapeHTML(image)}" alt="${escapeHTML(p.title)}" loading="lazy" width="65" height="90"></a>` : '<span></span>'}<div><a href="${escapeHTML(p.url)}">${escapeHTML(p.title)}</a><div class="bc-prices">${this.money(variants[0].price)}</div><div class="bc-product-controls"><select aria-label="${escapeHTML(this.i18n.choose_variant)} ${escapeHTML(p.title)}">${variants.map(v=>`<option value="${v.id}" data-price="${v.price}">${escapeHTML(v.title==='Default Title' ? this.i18n.default_variant : v.title)}</option>`).join('')}</select><button type="button" data-add>${escapeHTML(this.i18n.add)}</button></div></div></article>`;
      }).join('');
      section.hidden=!products.length;
      if (this.busy) this.setBusy(true);
    }
  }
  customElements.define('boutique-cart',BoutiqueCart);
})();
